import { createContext, useContext, useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { CURRENCY_SYMBOLS } from "../data/networksMeta";
import { useLanguage } from "./LanguageContext";

const CurrencyContext = createContext(null);
const VALID_CURRENCIES = ["EUR", "USD"];

export const CurrencyProvider = ({ children }) => {
  const storedCurrency = localStorage.getItem("sb_currency");
  const [currency, setCurrencyState] = useState(VALID_CURRENCIES.includes(storedCurrency) ? storedCurrency : "EUR");
  const [rates, setRates] = useState({ EUR: 1, USD: 1.08 });
  const { language } = useLanguage();
  const didMountRef = useRef(false);

  useEffect(() => {
    api.get("/settings/public").then(({ data }) => {
      if (data?.currency_rates) setRates(data.currency_rates);
    }).catch(() => {});
  }, []);

  // Currency mirrors the active UI language (FR -> EUR, anything else -> USD).
  // Language itself is auto-detected on first visit in LanguageContext (IP
  // geolocation, then browser locale), so this naturally cascades on first
  // load. On the very first render, an explicit prior currency choice is kept.
  // Any language change AFTER that (manual toggle, or async geo/browser-locale
  // detection resolving) always forces currency to match, even overriding an
  // explicit prior choice — this is intentional. Display only — the amount
  // actually charged always stays in EUR (Mollie).
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      if (!VALID_CURRENCIES.includes(localStorage.getItem("sb_currency"))) {
        setCurrencyState(language === "fr" ? "EUR" : "USD");
      }
      return;
    }
    setCurrency(language === "fr" ? "EUR" : "USD");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  const setCurrency = (c) => {
    localStorage.setItem("sb_currency", c);
    setCurrencyState(c);
  };

  const convert = (priceEUR) => {
    const rate = rates[currency] || 1;
    return priceEUR * rate;
  };

  const format = (priceEUR) => {
    const value = convert(priceEUR);
    const symbol = CURRENCY_SYMBOLS[currency] || currency;
    return currency === "EUR" ? `${value.toFixed(2)} ${symbol}` : `${symbol}${value.toFixed(2)}`;
  };

  return (
    <CurrencyContext.Provider value={{ currency, setCurrency, rates, convert, format }}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => useContext(CurrencyContext);
